import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Link from 'next/link';
import DangerousWithSanitization from '@base/DangerousWithSanitization';
import { upscCmsContentToHtml } from './upscCmsMarkdownToHtml';

export type UpscTopicCardWhyInNewsVariant = 'compact' | 'detailed';

const COMPACT_MAX_LINES = 7;

const markdownContentStyles = css`
  p {
    margin: 0 0 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul,
  ol {
    margin: 8px 0 12px;
    padding-left: 20px;
  }

  li {
    margin-bottom: 4px;
  }

  img {
    display: block;
    max-width: 100%;
    height: auto;
    margin: 12px 0;
    border-radius: 8px;
  }
`;

const WhyBox = styled.div<{ $variant: UpscTopicCardWhyInNewsVariant }>`
  ${markdownContentStyles}

  ${({ $variant }) =>
    $variant === 'compact'
      ? css`
          padding: 12px 14px;
          border-left: 3px solid var(--color-accent-blue);
          border-radius: 0 8px 8px 0;
          font-size: 14px;
          line-height: 1.6;
          background: var(--color-i-blue-11);
          color: var(--color-text-secondary);
        `
      : css`
          padding: 12px 14px;
          border-radius: 0 8px 8px 0;
          font-size: 15px;
          line-height: 1.6;
          color: var(--color-i-all-black);
        `}
`;

const ReadMoreLink = styled(Link)`
  display: inline-block;
  margin-top: 8px;
  font-size: 11px;
  font-weight: 600;
  color: var(--color-accent-blue);
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export interface UpscTopicCardWhyInNewsProps {
  description: string;
  variant?: UpscTopicCardWhyInNewsVariant;
  detailPath?: string;
}

const UpscTopicCardWhyInNews = ({
  description,
  variant = 'detailed',
  detailPath
}: UpscTopicCardWhyInNewsProps) => {
  const isCompact = variant === 'compact';
  const showReadMoreLink = isCompact && Boolean(detailPath);
  return (
    <WhyBox $variant={variant}>
      <DangerousWithSanitization
        as="div"
        html={upscCmsContentToHtml(
          description,
          isCompact ? COMPACT_MAX_LINES : undefined
        )}
        sanitize
      />
      {showReadMoreLink ? (
        <ReadMoreLink href={detailPath}>Read more</ReadMoreLink>
      ) : null}
    </WhyBox>
  );
};

export default UpscTopicCardWhyInNews;
